import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './context/AuthContext';
import { db, requestNotificationPermission, onForegroundMessage, getDeviceId } from './firebase';
import {
  collection, doc, addDoc, updateDoc, onSnapshot,
  query, orderBy, serverTimestamp, setDoc, getDocs, deleteDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import APP_VERSION from './version';
import { initAlertAudio, isSoundEnabled, setSoundEnabled, playAlertSound, showSystemNotification } from './alerts';
import './App.css';

// A connected app is any Firebase project that writes `supportCases` — a CRM, a
// staff portal, a marketing site. The Firestore collection is still called
// `connectedCRMs` because renaming it would orphan every existing connection;
// only the wording in the UI is product-neutral.
const CONNECTION_KINDS = [
  { id: 'crm', label: 'CRM' },
  { id: 'portal', label: 'Portal' },
  { id: 'website', label: 'Website' },
  { id: 'other', label: 'Other' },
];
const kindLabel = (id) => (CONNECTION_KINDS.find((k) => k.id === id) || CONNECTION_KINDS[0]).label;

const EMPTY_CRM_FORM = { name: '', kind: 'crm', apiKey: '', projectId: '', storageBucket: '', authEmail: '', authPassword: '' };

function Icon({ name, size, style, className }) {
  return (
    <span className={`material-icons-outlined ${className || ''}`} style={{ fontSize: size || 20, ...style }}>
      {name}
    </span>
  );
}

function formatTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError('Invalid email or password');
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <Icon name="support_agent" className="icon-big" />
        <h1>Support Center</h1>
        <p>Sign in to manage support cases</p>
        {error && <div className="login-error">{error}</div>}
        <input className="login-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input className="login-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <button className="login-btn" type="submit" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

const SUPPORT_AGENT_EMAIL = 'support-agent@support-portal.app';

async function createSupportAgentInCRM(apiKey) {
  const pwd = Array.from(crypto.getRandomValues(new Uint8Array(15)), b => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'.charAt(b % 62)).join('');
  const res = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: SUPPORT_AGENT_EMAIL, password: pwd, returnSecureToken: true }),
  });
  const data = await res.json();
  if (data.error) {
    if (data.error.message === 'EMAIL_EXISTS') {
      return { exists: true };
    }
    throw new Error(data.error.message);
  }
  return { email: SUPPORT_AGENT_EMAIL, password: pwd };
}

function ConfirmDialog({ message, onYes, onNo, darkMode }) {
  return (
    <div className="modal-overlay" onClick={onNo} style={{ zIndex: 5000 }}>
      <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-icon">
          <Icon name="warning_amber" size={28} style={{ color: '#f59e0b' }} />
        </div>
        <p className="confirm-message">{message}</p>
        <div className="confirm-actions">
          <button className="btn-secondary" onClick={onNo}>Cancel</button>
          <button className="btn-danger-solid" onClick={onYes}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ onClose, connectedCRMs, crmApps, onReconnect, showConfirm }) {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_CRM_FORM);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  const handleEdit = (crm) => {
    setForm({ name: crm.name, kind: crm.kind || 'crm', apiKey: crm.apiKey, projectId: crm.projectId, storageBucket: crm.storageBucket || '', authEmail: crm.authEmail || '', authPassword: crm.authPassword || '' });
    setEditId(crm.id);
    setShowForm(true);
    setSaveStatus('');
  };

  const handleSave = async () => {
    if (!form.name || !form.apiKey || !form.projectId) return;
    setSaving(true);
    setSaveStatus('');
    let authEmail = form.authEmail;
    let authPassword = form.authPassword;

    if (!editId && !authEmail) {
      setSaveStatus('Creating support agent...');
      try {
        const result = await createSupportAgentInCRM(form.apiKey);
        if (result.exists) {
          setSaveStatus('Agent exists — enter password manually or recreate in Firebase Console');
          setSaving(false);
          return;
        }
        authEmail = result.email;
        authPassword = result.password;
        setSaveStatus('Agent created!');
      } catch (err) {
        setSaveStatus(`Failed: ${err.message}`);
        setSaving(false);
        return;
      }
    }

    const data = { name: form.name, kind: form.kind || 'crm', apiKey: form.apiKey, projectId: form.projectId, storageBucket: form.storageBucket || `${form.projectId}.firebasestorage.app`, authEmail, authPassword, updatedAt: serverTimestamp() };
    if (editId) {
      await updateDoc(doc(db, 'connectedCRMs', editId), data);
    } else {
      await addDoc(collection(db, 'connectedCRMs'), { ...data, order: connectedCRMs.length, createdAt: serverTimestamp() });
    }
    setForm(EMPTY_CRM_FORM);
    setShowForm(false);
    setEditId(null);
    setSaving(false);
    setSaveStatus('');
    onReconnect();
  };

  const handleDelete = async (id) => {
    if (!await showConfirm('Remove this connection?')) return;
    await deleteDoc(doc(db, 'connectedCRMs', id));
  };

  // Rewrite every position rather than swapping two: connections created before
  // ordering existed have no `order` at all, so a swap would leave holes.
  const handleMove = async (index, delta) => {
    const next = [...connectedCRMs];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    await Promise.all(
      next.map((crm, i) => updateDoc(doc(db, 'connectedCRMs', crm.id), { order: i }))
    );
  };

  const inp = (label, key, type = 'text', placeholder = '') => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>{label}</label>
      <input className="login-input" type={type} placeholder={placeholder} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} style={{ margin: 0 }} />
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3>{showForm ? (editId ? 'Edit app' : 'Add app') : 'Connected apps'}</h3>
          <button onClick={onClose}><Icon name="close" /></button>
        </div>
        <div className="modal-body">
          {!showForm ? (
            <>
              <div className="crm-list">
                {connectedCRMs.map((crm, i) => (
                  <div className="crm-item" key={crm.id}>
                    <div className="crm-reorder">
                      <button onClick={() => handleMove(i, -1)} disabled={i === 0} title="Move up"><Icon name="arrow_upward" size={14} /></button>
                      <button onClick={() => handleMove(i, 1)} disabled={i === connectedCRMs.length - 1} title="Move down"><Icon name="arrow_downward" size={14} /></button>
                    </div>
                    <div className="crm-item-info">
                      <h4>
                        {crm.name}
                        <span className="crm-kind-badge">{kindLabel(crm.kind)}</span>
                      </h4>
                      <p>{crm.projectId}</p>
                    </div>
                    <div className="crm-item-actions" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span className="crm-status-badge" style={{ background: crmApps[crm.id] ? '#2e7d32' : '#c62828', color: '#fff' }}>
                        {crmApps[crm.id] ? 'Connected' : 'Not connected'}
                      </span>
                      <button onClick={() => handleEdit(crm)} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}><Icon name="edit" size={16} /></button>
                      <button onClick={() => handleDelete(crm.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 4 }}><Icon name="delete" size={16} /></button>
                    </div>
                  </div>
                ))}
                {connectedCRMs.length === 0 && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                    No apps connected yet. Click "Add app" to connect a CRM, portal or website.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div>
              {inp('App name *', 'name', 'text', 'e.g. Staff Portal — Info Site')}
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Type</label>
                <select className="login-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} style={{ margin: 0 }}>
                  {CONNECTION_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
              </div>
              {inp('API Key *', 'apiKey', 'text', 'AIzaSy...')}
              {inp('Project ID *', 'projectId', 'text', 'my-firebase-project')}
              {inp('Storage Bucket', 'storageBucket', 'text', 'auto-filled if empty')}
              {editId && (
                <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0', paddingTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Support Agent Credentials</div>
                  {inp('Email', 'authEmail', 'email', 'support-agent@...')}
                  {inp('Password', 'authPassword', 'password', '••••••')}
                </div>
              )}
              {!editId && (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 12, lineHeight: 1.5 }}>
                  <Icon name="info" size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
                  A support agent account will be auto-created in this app's Firebase Auth. No license is consumed — it's auth-only. The app's Firestore rules must grant that account access to supportCases.
                </p>
              )}
              {saveStatus && <p style={{ fontSize: 12, color: 'var(--primary)', marginTop: 8 }}>{saveStatus}</p>}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {!showForm ? (
            <>
              <button className="btn-primary" onClick={() => { setForm(EMPTY_CRM_FORM); setEditId(null); setShowForm(true); setSaveStatus(''); }}><Icon name="add" size={16} /> Add app</button>
              <button className="btn-secondary" onClick={onReconnect} style={{ marginLeft: 8 }}><Icon name="refresh" size={16} /> Reconnect</button>
            </>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => { setShowForm(false); setEditId(null); setSaveStatus(''); }}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving || !form.name || !form.apiKey || !form.projectId} style={{ marginLeft: 8 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ImagePreview({ src, onClose }) {
  if (!src) return null;
  return (
    <div className="image-preview-overlay" onClick={onClose}>
      <img src={src} alt="Preview" />
    </div>
  );
}

function ToastContainer({ toasts, onDismiss }) {
  return (
    <div className="toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.type || ''}`} onClick={() => onDismiss(t.id)}>
          <Icon name={t.icon || 'notifications'} size={20} style={{ color: t.type === 'error' ? '#d32f2f' : 'var(--primary)' }} />
          <div className="toast-text">
            <h4>{t.title}</h4>
            <p>{t.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// Pops over everything when a ticket arrives and stays until the agent acts —
// a toast that fades after five seconds is too easy to miss. Newest first.
function NewTicketAlert({ alerts, onOpen, onDismiss }) {
  useEffect(() => {
    if (alerts.length === 0) return;
    const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [alerts.length, onDismiss]);

  if (alerts.length === 0) return null;
  const [latest, ...older] = alerts;
  return (
    <div className="modal-overlay ticket-alert-overlay">
      <div className="ticket-alert" role="alertdialog" aria-modal="true" aria-labelledby="ticket-alert-title">
        <div className="ticket-alert-head">
          <div className="ticket-alert-bell"><Icon name="notifications_active" size={26} /></div>
          <div>
            <h3 id="ticket-alert-title">{alerts.length === 1 ? 'New support ticket' : `${alerts.length} new support tickets`}</h3>
            <p>{formatTime(latest.createdAt) || 'Just now'}</p>
          </div>
        </div>
        <div className="ticket-alert-case" onClick={() => onOpen(latest)}>
          <div className="ticket-alert-row">
            <span className="case-number">#{latest.caseNumber}</span>
            <span className="crm-kind-badge">{latest.crmName}</span>
          </div>
          <div className="ticket-alert-subject">{latest.subject || latest.category || 'No subject'}</div>
          <div className="ticket-alert-meta">
            {latest.openedBy && <span><Icon name="person" size={14} /> {latest.openedBy}</span>}
            {latest.category && <span><Icon name="category" size={14} /> {latest.category}</span>}
          </div>
        </div>
        {older.length > 0 && (
          <div className="ticket-alert-more">
            {older.slice(0, 4).map((c) => (
              <button key={`${c.crmId}-${c.id}`} onClick={() => onOpen(c)}>
                <span className="case-number">#{c.caseNumber}</span>
                <span className="ticket-alert-more-text">{c.crmName} — {c.subject || c.category}</span>
                <Icon name="chevron_right" size={16} />
              </button>
            ))}
            {older.length > 4 && <div className="ticket-alert-overflow">+{older.length - 4} more in the sidebar</div>}
          </div>
        )}
        <div className="ticket-alert-actions">
          <button className="btn-secondary" onClick={onDismiss}>{alerts.length > 1 ? 'Dismiss all' : 'Dismiss'}</button>
          <button className="btn-primary" onClick={() => onOpen(latest)} autoFocus><Icon name="open_in_new" size={16} /> Open case</button>
        </div>
      </div>
    </div>
  );
}

// Shown only while alerts can't fire: audio still locked by the browser, or
// notification permission never asked. The click itself is the gesture that
// unlocks both.
function AlertsBanner({ soundOn, audioReady, notifPermission, onEnable }) {
  const needNotif = notifPermission === 'default';
  const needSound = soundOn && !audioReady;
  if (!needNotif && !needSound) return null;
  return (
    <button className="alerts-banner" onClick={onEnable}>
      <Icon name={needNotif ? 'notifications_off' : 'volume_off'} size={18} />
      <span>{needNotif ? 'Turn on ticket alerts' : 'Sound alerts are paused — click to enable'}</span>
    </button>
  );
}

function App() {
  const { user, logout, loading: authLoading } = useAuth();
  const [connectedCRMs, setConnectedCRMs] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('support-collapsed-groups') || '[]'); }
    catch { return []; }
  });
  const toggleGroup = (id) => setCollapsedGroups((prev) => {
    const next = prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id];
    try { localStorage.setItem('support-collapsed-groups', JSON.stringify(next)); } catch {}
    return next;
  });
  const [crmApps, setCrmApps] = useState({});
  const [crmLoading, setCrmLoading] = useState(true);
  const [allCases, setAllCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');

  const [confirmDialog, setConfirmDialog] = useState(null);
  const [ticketAlerts, setTicketAlerts] = useState([]);
  const [soundOn, setSoundOn] = useState(isSoundEnabled);
  const [audioReady, setAudioReady] = useState(false);
  const [notifPermission, setNotifPermission] = useState(() => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'));
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState === 'visible');
  const [pendingOpenCaseId, setPendingOpenCaseId] = useState(() => new URLSearchParams(window.location.search).get('case'));

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const unsubCasesRef = useRef([]);
  const unsubMessagesRef = useRef(null);
  const toastIdRef = useRef(0);
  const selectedCaseRef = useRef(null);
  const unreadSeenRef = useRef(new Map());
  const alertHandlersRef = useRef({});
  const alertedRef = useRef(new Map());
  selectedCaseRef.current = selectedCase;

  // A push for a case usually lands a second or two after the Firestore snapshot
  // for the same change. Whichever path claims the case first alerts; a claim
  // inside 15s tells the other one to stay quiet.
  const claimAlert = useCallback((caseId) => {
    const now = Date.now();
    const last = alertedRef.current.get(caseId);
    alertedRef.current.set(caseId, now);
    return !last || now - last > 15000;
  }, []);

  useEffect(() => initAlertAudio(setAudioReady), []);

  useEffect(() => {
    const onVis = () => setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // ?case= is only a hand-off from a notification tap; drop it so a reload
  // doesn't reopen that case.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('case')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    const headerColor = darkMode ? '#0d47a1' : '#1976d2';
    let metas = document.querySelectorAll('meta[name="theme-color"]');
    metas.forEach(m => m.parentNode.removeChild(m));
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = headerColor;
    document.head.appendChild(meta);
  }, [darkMode]);

  const showConfirm = useCallback((msg) => {
    return new Promise((resolve) => {
      setConfirmDialog({ msg, resolve });
    });
  }, []);
  const handleConfirmYes = useCallback(() => {
    if (confirmDialog?.resolve) confirmDialog.resolve(true);
    setConfirmDialog(null);
  }, [confirmDialog]);
  const handleConfirmNo = useCallback(() => {
    if (confirmDialog?.resolve) confirmDialog.resolve(false);
    setConfirmDialog(null);
  }, [confirmDialog]);

  const addToast = useCallback((title, body, type = 'info', icon = 'notifications') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, title, body, type, icon }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  // Load CRM connections from Firestore; seed from env on first run
  useEffect(() => {
    if (!user) return;
    let seeded = false;
    const unsub = onSnapshot(collection(db, 'connectedCRMs'), async (snap) => {
      if (snap.empty && !seeded) {
        seeded = true;
        for (let i = 1; i <= 20; i++) {
          const name = process.env[`REACT_APP_CRM_${i}_NAME`];
          const apiKey = process.env[`REACT_APP_CRM_${i}_API_KEY`];
          const projectId = process.env[`REACT_APP_CRM_${i}_PROJECT_ID`];
          if (!name || !apiKey || !projectId) continue;
          await addDoc(collection(db, 'connectedCRMs'), {
            name, apiKey, projectId,
            storageBucket: process.env[`REACT_APP_CRM_${i}_STORAGE_BUCKET`] || `${projectId}.firebasestorage.app`,
            authEmail: process.env[`REACT_APP_CRM_${i}_AUTH_EMAIL`] || '',
            authPassword: process.env[`REACT_APP_CRM_${i}_AUTH_PASSWORD`] || '',
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          });
        }
        return;
      }
      // Sidebar groups follow this order, so sort once here rather than at every
      // render site. Connections saved before ordering existed have no `order`
      // and fall to the end, alphabetically.
      const crms = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      crms.sort((a, b) => {
        const oa = typeof a.order === 'number' ? a.order : 9999;
        const ob = typeof b.order === 'number' ? b.order : 9999;
        return oa - ob || (a.name || '').localeCompare(b.name || '');
      });
      setConnectedCRMs(crms);
      setCrmLoading(false);
    });
    return () => unsub();
  }, [user]);

  // Initialize secondary Firebase apps for each CRM + sign in
  const initCRMApps = useCallback(async (crms) => {
    const apps = {};
    for (const crm of crms) {
      try {
        const config = {
          apiKey: crm.apiKey,
          authDomain: `${crm.projectId}.firebaseapp.com`,
          projectId: crm.projectId,
          storageBucket: crm.storageBucket || `${crm.projectId}.firebasestorage.app`,
        };
        let app;
        try { app = initializeApp(config, crm.id); } catch (e) {
          if (e.code === 'app/duplicate-app') {
            const { getApp } = require('firebase/app');
            app = getApp(crm.id);
          } else throw e;
        }
        const crmAuth = getAuth(app);
        if (crm.authEmail && crm.authPassword) {
          try { await signInWithEmailAndPassword(crmAuth, crm.authEmail, crm.authPassword); }
          catch (authErr) { console.error(`Auth failed for ${crm.name}:`, authErr.message); }
        }
        apps[crm.id] = { app, firestore: getFirestore(app), storage: getStorage(app), name: crm.name };
      } catch (e) {
        console.error(`Failed to init CRM ${crm.name}:`, e);
      }
    }
    setCrmApps(apps);
  }, []);

  useEffect(() => {
    if (connectedCRMs.length > 0) initCRMApps(connectedCRMs);
    else if (!crmLoading) setCrmApps({});
  }, [connectedCRMs, initCRMApps, crmLoading]);

  const handleReconnect = () => { initCRMApps(connectedCRMs); };

  // Subscribe to supportCases from all connected CRMs
  useEffect(() => {
    unsubCasesRef.current.forEach((u) => u());
    unsubCasesRef.current = [];
    if (Object.keys(crmApps).length === 0) { setAllCases([]); return; }

    Object.entries(crmApps).forEach(([crmId, { firestore, name }]) => {
      const q = query(collection(firestore, 'supportCases'), orderBy('createdAt', 'desc'));
      // The first snapshot is the existing backlog, not news — only changes after
      // it raise alerts. Tracked per listener, so a second app's initial load
      // doesn't read as a flood of new tickets.
      let baseline = true;
      const unsub = onSnapshot(q, (snap) => {
        const cases = snap.docs.map((d) => ({ id: d.id, crmId, crmName: name, ...d.data() }));
        const seen = unreadSeenRef.current;
        if (!baseline) {
          snap.docChanges().forEach((ch) => {
            const c = { id: ch.doc.id, crmId, crmName: name, ...ch.doc.data() };
            if (ch.type === 'added') alertHandlersRef.current.newCase?.(c);
            else if (ch.type === 'modified' && (c.unreadBySupport || 0) > (seen.get(`${crmId}/${c.id}`) || 0)) {
              alertHandlersRef.current.newMessage?.(c);
            }
          });
        }
        baseline = false;
        cases.forEach((c) => seen.set(`${crmId}/${c.id}`, c.unreadBySupport || 0));
        setAllCases((prev) => {
          const merged = [...prev.filter((c) => c.crmId !== crmId), ...cases];
          merged.sort((a, b) => {
            const ta = a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || 0;
            const tb = b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || 0;
            return tb - ta;
          });
          return merged;
        });
      }, (err) => console.error(`Error listening to cases from ${name}:`, err));
      unsubCasesRef.current.push(unsub);
    });
    return () => unsubCasesRef.current.forEach((u) => u());
  }, [crmApps]);

  // Subscribe to messages for selected case
  useEffect(() => {
    if (unsubMessagesRef.current) { unsubMessagesRef.current(); unsubMessagesRef.current = null; }
    if (!selectedCase) { setMessages([]); return; }
    const crmApp = crmApps[selectedCase.crmId];
    if (!crmApp) return;

    const q = query(collection(crmApp.firestore, 'supportCases', selectedCase.id, 'messages'), orderBy('timestamp', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    unsubMessagesRef.current = unsub;
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCase?.id, selectedCase?.crmId]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Notifications — register FCM token + per-device record so cloud functions can target specific devices.
  // Also re-registers when the user returns to the app (iOS ITP may wipe the push subscription).
  const registerPushToken = useCallback(async () => {
    if (!user) return;
    const token = await requestNotificationPermission();
    if (typeof Notification !== 'undefined') setNotifPermission(Notification.permission);
    if (!token) return;
    const deviceId = getDeviceId();
    // Primary per-device record (cloud function reads this collection to send pushes).
    await setDoc(doc(db, 'supportAgents', user.uid, 'devices', deviceId), {
      fcmToken: token,
      ua: navigator.userAgent,
      updatedAt: serverTimestamp(),
    }, { merge: true }).catch(() => {});
    // Legacy/fallback: also keep latest token at agent root.
    await setDoc(doc(db, 'supportAgents', user.uid), {
      fcmToken: token,
      email: user.email,
      lastDeviceId: deviceId,
      updatedAt: serverTimestamp(),
    }, { merge: true }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    registerPushToken();
    const onVis = () => { if (document.visibilityState === 'visible') registerPushToken(); };
    document.addEventListener('visibilitychange', onVis);
    // While the console is open the live listeners already alert on everything
    // a push carries, with the full case. So a push waits a moment and only
    // speaks up if no listener claimed its case — e.g. an app whose sign-in failed.
    const onPush = (title, body, caseId) => {
      setTimeout(() => {
        if (caseId && !claimAlert(caseId)) return;
        addToast(title || 'New Message', body || '', 'info', 'chat');
        playAlertSound();
      }, 3000);
    };
    const unsub = onForegroundMessage((payload) => {
      onPush(payload.notification?.title || payload.data?.title,
             payload.notification?.body || payload.data?.body,
             payload.data?.caseId);
    });
    const onSwMessage = (event) => {
      if (event.data?.type === 'PUSH_RECEIVED') onPush(event.data.title, event.data.body, event.data.caseId);
      else if (event.data?.type === 'OPEN_CASE' && event.data.caseId) setPendingOpenCaseId(event.data.caseId);
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
      if (typeof unsub === 'function') unsub();
    };
  }, [user, addToast, registerPushToken, claimAlert]);

  // Called from the snapshot listeners through a ref, so they always see the
  // current selection without the listeners having to resubscribe.
  alertHandlersRef.current = {
    newCase: (c) => {
      claimAlert(c.id);
      setTicketAlerts((prev) => [c, ...prev.filter((a) => !(a.id === c.id && a.crmId === c.crmId))]);
      playAlertSound({ times: 3 });
      if (document.visibilityState !== 'visible') {
        showSystemNotification(`New Case #${c.caseNumber} — ${c.crmName}`, { body: c.subject || c.category || 'New support case', caseId: c.id });
      }
    },
    newMessage: (c) => {
      const open = selectedCaseRef.current;
      if (document.visibilityState === 'visible' && open?.id === c.id && open?.crmId === c.crmId) return;
      claimAlert(c.id);
      addToast(`New message — Case #${c.caseNumber}`, `${c.crmName} — ${c.lastMessage || ''}`, 'info', 'chat');
      playAlertSound();
      if (document.visibilityState !== 'visible') {
        showSystemNotification(`Case #${c.caseNumber} — ${c.crmName}`, { body: c.lastMessage || 'New message', caseId: c.id });
      }
    },
  };

  const handleSelectCase = (c) => {
    setSelectedCase(c);
    setSidebarHidden(true);
    setTicketAlerts((prev) => prev.filter((a) => !(a.id === c.id && a.crmId === c.crmId)));
  };

  const dismissTicketAlerts = useCallback(() => setTicketAlerts([]), []);
  const openFromAlert = (c) => { handleSelectCase(c); setTicketAlerts([]); };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundEnabled(next);
    if (next) playAlertSound({ force: true });
  };

  // Runs inside the click, so it counts as the gesture both audio and the
  // permission prompt need. The test chime confirms sound is working.
  const enableAlerts = async () => {
    playAlertSound({ force: true });
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { await Notification.requestPermission(); } catch {}
    }
    if (typeof Notification !== 'undefined') setNotifPermission(Notification.permission);
    registerPushToken();
  };

  // Tapping a system notification opens its case — via ?case= on a cold start,
  // or an OPEN_CASE message from the service worker when the app is already open.
  // Waits for the case to show up, since a cold start has no cases yet.
  useEffect(() => {
    if (!pendingOpenCaseId) return;
    const c = allCases.find((x) => x.id === pendingOpenCaseId);
    if (!c) return;
    handleSelectCase(c);
    setPendingOpenCaseId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenCaseId, allCases]);

  const handleSendMessage = async () => {
    if ((!messageText.trim() && attachments.length === 0) || !selectedCase) return;
    setSending(true);
    const crmApp = crmApps[selectedCase.crmId];
    if (!crmApp) { setSending(false); return; }
    try {
      let uploadedFiles = [];
      for (const file of attachments) {
        const path = `supportFiles/${selectedCase.id}/${Date.now()}_${file.name}`;
        const storageRef = ref(crmApp.storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        uploadedFiles.push({ name: file.name, url, type: file.type, size: file.size });
      }
      await addDoc(collection(crmApp.firestore, 'supportCases', selectedCase.id, 'messages'), {
        text: messageText.trim(), sender: 'support', senderName: 'Support Team', timestamp: serverTimestamp(), attachments: uploadedFiles,
      });
      await updateDoc(doc(crmApp.firestore, 'supportCases', selectedCase.id), {
        lastMessage: messageText.trim() || `Sent ${uploadedFiles.length} file(s)`,
        updatedAt: serverTimestamp(),
        unreadByUser: (selectedCase.unreadByUser || 0) + 1,
        status: selectedCase.status === 'open' ? 'in-progress' : selectedCase.status,
      });
      setMessageText(''); setAttachments([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    } catch (err) {
      console.error('Send error:', err);
      addToast('Send Failed', err.message, 'error', 'error');
    }
    setSending(false);
  };

  const handleCloseCase = async () => {
    if (!selectedCase) return;
    const crmApp = crmApps[selectedCase.crmId];
    if (!crmApp) return;
    await updateDoc(doc(crmApp.firestore, 'supportCases', selectedCase.id), { status: 'closed', closedAt: serverTimestamp(), updatedAt: serverTimestamp() });
    await addDoc(collection(crmApp.firestore, 'supportCases', selectedCase.id, 'messages'), { text: 'Case has been closed by support team.', sender: 'system', timestamp: serverTimestamp(), attachments: [] });
    addToast('Case Closed', `Case #${selectedCase.caseNumber} closed`, 'success', 'check_circle');
  };

  const handleReopenCase = async () => {
    if (!selectedCase) return;
    const crmApp = crmApps[selectedCase.crmId];
    if (!crmApp) return;
    await updateDoc(doc(crmApp.firestore, 'supportCases', selectedCase.id), { status: 'open', closedAt: null, updatedAt: serverTimestamp() });
    await addDoc(collection(crmApp.firestore, 'supportCases', selectedCase.id, 'messages'), { text: 'Case has been reopened by support team.', sender: 'system', timestamp: serverTimestamp(), attachments: [] });
  };

  const handleDeleteCase = async () => {
    if (!selectedCase || !await showConfirm(`Delete Case #${selectedCase.caseNumber}? This cannot be undone.`)) return;
    const crmApp = crmApps[selectedCase.crmId];
    if (!crmApp) return;
    const msgsSnap = await getDocs(collection(crmApp.firestore, 'supportCases', selectedCase.id, 'messages'));
    for (const m of msgsSnap.docs) await deleteDoc(m.ref);
    await deleteDoc(doc(crmApp.firestore, 'supportCases', selectedCase.id));
    addToast('Case Deleted', `Case #${selectedCase.caseNumber} deleted`, 'info', 'delete');
    setSelectedCase(null);
    setSidebarHidden(false);
  };

  const handleFileSelect = (e) => { setAttachments((prev) => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; };
  const removeAttachment = (idx) => { setAttachments((prev) => prev.filter((_, i) => i !== idx)); };
  const handleTextareaResize = (e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };

  const filteredCases = allCases.filter((c) => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (c.caseNumber?.toString() || '').includes(q) || (c.crmName || '').toLowerCase().includes(q) || (c.subject || '').toLowerCase().includes(q) || (c.category || '').toLowerCase().includes(q) || (c.openedBy || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Sidebar is grouped by app, in the order set under Connected apps. Cases from
  // a connection that has since been removed still need somewhere to live, so
  // anything unmatched is collected into a trailing group.
  const caseGroups = (() => {
    const byCrm = new Map();
    filteredCases.forEach((c) => {
      if (!byCrm.has(c.crmId)) byCrm.set(c.crmId, []);
      byCrm.get(c.crmId).push(c);
    });
    const groups = connectedCRMs
      .filter((crm) => byCrm.has(crm.id))
      .map((crm) => ({
        id: crm.id,
        name: crm.name,
        kind: crm.kind,
        cases: byCrm.get(crm.id),
      }));
    const known = new Set(connectedCRMs.map((crm) => crm.id));
    [...byCrm.entries()]
      .filter(([crmId]) => !known.has(crmId))
      .forEach(([crmId, cases]) => {
        groups.push({ id: crmId, name: cases[0]?.crmName || 'Disconnected', kind: null, cases });
      });
    return groups;
  })();

  // One connection means every header would say the same thing — not worth the
  // vertical space. A search should also reach into collapsed groups.
  const showGroupHeaders = caseGroups.length > 1;

  const totalUnread = allCases.reduce((sum, c) => sum + (c.unreadBySupport || 0), 0);
  const unreadCasesCount = allCases.filter((c) => (c.unreadBySupport || 0) > 0).length;
  useEffect(() => { document.title = totalUnread > 0 ? `(${totalUnread}) Support Center` : 'Support Center'; }, [totalUnread]);

  // PWA app icon badge — count of unread tickets. Clears when all read.
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    if (unreadCasesCount > 0) {
      navigator.setAppBadge(unreadCasesCount).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  }, [unreadCasesCount]);

  // Re-sync badge when user returns to the app (e.g. from another tab)
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      if (!('setAppBadge' in navigator)) return;
      if (unreadCasesCount > 0) navigator.setAppBadge(unreadCasesCount).catch(() => {});
      else navigator.clearAppBadge().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [unreadCasesCount]);

  // Keep the open case at zero unread while the agent is looking at it. Before,
  // this only ran when the case was first opened, so messages arriving mid-
  // conversation left a badge behind.
  const liveSelected = selectedCase ? allCases.find((c) => c.id === selectedCase.id && c.crmId === selectedCase.crmId) : null;
  useEffect(() => {
    if (!liveSelected || !pageVisible || !((liveSelected.unreadBySupport || 0) > 0)) return;
    const crmApp = crmApps[liveSelected.crmId];
    if (!crmApp) return;
    updateDoc(doc(crmApp.firestore, 'supportCases', liveSelected.id), { unreadBySupport: 0 }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSelected?.id, liveSelected?.crmId, liveSelected?.unreadBySupport, pageVisible, crmApps]);

  if (authLoading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!user) return <LoginPage />;

  const groupedMessages = [];
  let lastDate = '';
  messages.forEach((msg) => {
    const dateStr = formatDate(msg.timestamp);
    if (dateStr !== lastDate) { groupedMessages.push({ type: 'date', date: dateStr }); lastDate = dateStr; }
    groupedMessages.push({ type: 'message', ...msg });
  });

  const currentCase = liveSelected || selectedCase;

  return (
    <>
      <div className="app-layout">
        <div className={`sidebar ${sidebarHidden ? 'hidden' : ''}`}>
          <div className="sidebar-header">
            <button
              onClick={() => window.location.reload()}
              title="Reload app"
              className="logo-reload-btn"
            >
              <h2><Icon name="support_agent" size={22} /> Support</h2>
              {totalUnread > 0 && <span className="header-unread-badge">{totalUnread}</span>}
            </button>
            <div className="sidebar-header-actions">
              <button onClick={() => setDarkMode(!darkMode)} title={darkMode ? 'Light mode' : 'Dark mode'}>
                <Icon name={darkMode ? 'light_mode' : 'dark_mode'} size={20} />
              </button>
              <button onClick={toggleSound} title={soundOn ? 'Mute alert sound' : 'Unmute alert sound'}>
                <Icon name={soundOn ? 'volume_up' : 'volume_off'} size={20} />
              </button>
              <button onClick={() => setShowSettings(true)} title="Connected apps">
                <Icon name="settings" size={20} />
              </button>
            </div>
          </div>

          <AlertsBanner soundOn={soundOn} audioReady={audioReady} notifPermission={notifPermission} onEnable={enableAlerts} />

          <div className="sidebar-filters">
            {['all', 'open', 'in-progress', 'closed'].map((f) => (
              <button key={f} className={`filter-chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'in-progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                {f !== 'all' && <span style={{ marginLeft: 4 }}>({allCases.filter((c) => c.status === f).length})</span>}
              </button>
            ))}
          </div>

          <div className="sidebar-search">
            <div className="search-wrapper">
              <Icon name="search" />
              <input placeholder="Search cases..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
          </div>

          <div className="case-list">
            {filteredCases.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)', fontSize: 13 }}>
                {allCases.length === 0 ? 'No cases yet' : 'No matching cases'}
              </div>
            )}
            {caseGroups.map((group) => {
              const groupUnread = group.cases.reduce((n, c) => n + (c.unreadBySupport || 0), 0);
              const collapsed = showGroupHeaders && !searchQuery && collapsedGroups.includes(group.id);
              return (
                <div className="case-group" key={group.id}>
                  {showGroupHeaders && (
                    <button
                      className={`case-group-header ${collapsed ? 'collapsed' : ''}`}
                      onClick={() => toggleGroup(group.id)}
                      title={collapsed ? 'Expand' : 'Collapse'}
                    >
                      <Icon name="expand_more" size={18} className="case-group-chevron" />
                      <span className="case-group-name">{group.name}</span>
                      {group.kind && <span className="crm-kind-badge">{kindLabel(group.kind)}</span>}
                      <span className="case-group-count">{group.cases.length}</span>
                      {groupUnread > 0 && <span className="case-group-unread">{groupUnread}</span>}
                    </button>
                  )}
                  {!collapsed && group.cases.map((c) => (
                    <div key={`${c.crmId}-${c.id}`} className={`case-item ${currentCase?.id === c.id && currentCase?.crmId === c.crmId ? 'active' : ''}`} onClick={() => handleSelectCase(c)}>
                      <div className="case-item-header">
                        <span className="case-number">#{c.caseNumber}</span>
                        <span className="case-time">{formatRelative(c.updatedAt || c.createdAt)}</span>
                      </div>
                      <div className="case-company">{showGroupHeaders ? (c.openedBy || c.crmName) : `${c.crmName}${c.openedBy ? ` — ${c.openedBy}` : ''}`}</div>
                      <div className="case-preview">{c.subject || c.lastMessage || c.category}</div>
                      <div className="case-meta">
                        <span className={`case-status ${c.status}`}>{c.status === 'in-progress' ? 'In Progress' : c.status}</span>
                        <span className="case-category-chip">{c.category}</span>
                      </div>
                      {(c.unreadBySupport || 0) > 0 && <div className="unread-badge">{c.unreadBySupport}</div>}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="sidebar-footer">
            <span className="version">v{APP_VERSION}</span>
            <button onClick={logout}><Icon name="logout" size={16} /> Logout</button>
          </div>
        </div>

        <div className="main-content">
          {!currentCase ? (
            <div className="empty-state">
              <Icon name="support_agent" />
              <h3>Support Center</h3>
              <p>Select a case from the sidebar to view details</p>
            </div>
          ) : (
            <>
              <div className="case-header">
                <div className="case-header-left">
                  <h3>
                    <button className="mobile-back-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-primary)' }} onClick={() => { setSelectedCase(null); setSidebarHidden(false); }}>
                      <Icon name="arrow_back" size={20} />
                    </button>
                    Case #{currentCase.caseNumber}
                    <span className={`case-status ${currentCase.status}`}>{currentCase.status === 'in-progress' ? 'In Progress' : currentCase.status}</span>
                  </h3>
                  <div className="case-info">
                    <span><Icon name="business" size={14} style={{ verticalAlign: -2 }} /> {currentCase.crmName}</span>
                    {currentCase.openedBy && <span><Icon name="person" size={14} style={{ verticalAlign: -2 }} /> {currentCase.openedBy}</span>}
                    <span><Icon name="category" size={14} style={{ verticalAlign: -2 }} /> {currentCase.category}</span>
                    <span><Icon name="schedule" size={14} style={{ verticalAlign: -2 }} /> {formatDate(currentCase.createdAt)}</span>
                  </div>
                </div>
                <div className="case-header-right" style={{ display: 'flex', gap: 8 }}>
                  {currentCase.status !== 'closed' ? (
                    <button className="status-btn close-btn" onClick={handleCloseCase}><Icon name="check_circle" size={16} /> Close</button>
                  ) : (
                    <button className="status-btn reopen-btn" onClick={handleReopenCase}><Icon name="refresh" size={16} /> Reopen</button>
                  )}
                  <button className="status-btn" onClick={handleDeleteCase} style={{ background: 'var(--danger)', color: '#fff', borderColor: 'var(--danger)' }}><Icon name="delete" size={16} /> Delete</button>
                </div>
              </div>

              <div className="chat-area">
                {currentCase.subject && (
                  <div className="subject-card">
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Subject</div>
                    <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{currentCase.subject}</div>
                  </div>
                )}
                {groupedMessages.map((item, idx) => {
                  if (item.type === 'date') return <div className="chat-date-divider" key={`date-${idx}`}><span>{item.date}</span></div>;
                  return (
                    <div key={item.id} className={`message ${item.sender}`}>
                      <div>
                        <div className="message-bubble">
                          {item.sender === 'user' && item.senderName && <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, opacity: 0.7 }}>{item.senderName}</div>}
                          {item.text && <div>{item.text}</div>}
                          {item.attachments?.map((att, ai) => (
                            <div className="message-attachment" key={ai}>
                              {att.type?.startsWith('image/') ? (
                                <img src={att.url} alt={att.name} onClick={() => setPreviewImage(att.url)} />
                              ) : (
                                <a href={att.url} target="_blank" rel="noopener noreferrer" className="file-link"><Icon name="attach_file" size={16} />{att.name}</a>
                              )}
                            </div>
                          ))}
                          <div className="message-time">{formatTime(item.timestamp)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>

              {currentCase.status !== 'closed' && (
                <div className="chat-input-area">
                  {attachments.length > 0 && (
                    <div className="attachment-preview">
                      {attachments.map((file, idx) => (
                        <div className="attachment-thumb" key={idx}>
                          {file.type?.startsWith('image/') ? <img src={URL.createObjectURL(file)} alt={file.name} /> : <div className="file-name"><Icon name="attach_file" size={14} />{file.name}</div>}
                          <button className="remove-btn" onClick={() => removeAttachment(idx)}><Icon name="close" size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="chat-input-wrapper">
                    <button onClick={() => fileInputRef.current?.click()} title="Attach file"><Icon name="attach_file" size={22} /></button>
                    <textarea ref={textareaRef} value={messageText} onChange={(e) => { setMessageText(e.target.value); handleTextareaResize(e); }} onKeyDown={handleKeyDown} placeholder="Type a message..." rows={1} />
                    <button className="send-btn" onClick={handleSendMessage} disabled={sending || (!messageText.trim() && attachments.length === 0)} title="Send"><Icon name="send" size={22} /></button>
                  </div>
                  <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileSelect} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} connectedCRMs={connectedCRMs} crmApps={crmApps} onReconnect={handleReconnect} showConfirm={showConfirm} />}
      {confirmDialog && <ConfirmDialog message={confirmDialog.msg} onYes={handleConfirmYes} onNo={handleConfirmNo} darkMode={darkMode} />}
      <ImagePreview src={previewImage} onClose={() => setPreviewImage(null)} />
      <ToastContainer toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />
      <NewTicketAlert alerts={ticketAlerts} onOpen={openFromAlert} onDismiss={dismissTicketAlerts} />
    </>
  );
}

export default App;
